import type { Subset } from "@/helpers/utility/Type";
import { defineItemStore } from "../itemStore.store";
import type { SceneItem } from "./SceneItem";
import { useSceneLayerStore } from "../sceneLayer/sceneLayers.store";
import { useSceneNodesStore } from "./sceneNodes.store";
import { showMessage } from "@core/ui/alert/alert";
import { usePresenceStore } from "@editor/store/presence.store";
import { i18n } from "@/i18n";
import { useSceneFlowNodeStore } from "../flow/sceneFlowNodes";

const defaultSceneItem: SceneItem = {
  id: 0,
  uid: "scene_0",
  type: "scene",
  title: "New Scene",

  editModeOnly: false,

  translateX: 0,
  translateY: 0,
  scale: 0.6,

  templateId: null,
  areasVisible: true,
  placeholdersVisible: true,

  backgroundColor: "#ffffff",
  backgroundImageId: null,
  fontSize: 16,

  indexConnectionsVisible: true,
  linkRelationsDisplay: "visible",
  flowRelationsDisplay: "visible",
  elementsDisplayMode: "value",
  footerAnchored: false,

  createdAt: Date.now(),
};

export const useSceneStore = defineItemStore("model/scene", defaultSceneItem, {
  state(baseState) {
    return {
      ...baseState,
    }
  },
  getters: {
    activeScene() {
      const presence = usePresenceStore();
      const activeSceneId = presence.activeSceneId;
      const scene = this.items[activeSceneId];
      if (!scene) {
        console.warn(`Scene ${activeSceneId} not found, defaulting to first scene.`);
        return this.orderedItems[0] || null;
      }
      return scene || null;
    },
    titles() {
      const titles: { [sceneId: number | string]: string } = {};
      for (const scene of this.orderedItems) {
        titles[scene.id] = scene.title || i18n.t('objectNames.scene') + ` ${scene.id + 1}`;
      }
      return titles;
    }
  },
  actions: {
    async createWithDefaultLayers(createData: Subset<SceneItem>) {
      const newScene = await this.super.create(createData);
      const layerStore = useSceneLayerStore();
      layerStore.create({
        sceneId: newScene.id,
        order: 300
      }, {
        layerType: "foreground"
      });
      layerStore.create({
        sceneId: newScene.id,
        order: 200
      }, {
        layerType: "scene",
      });
      layerStore.create({
        sceneId: newScene.id,
        order: 100
      }, {
        layerType: "links"
      });
      return newScene;
    },

    async duplicate(sceneId: number, changes?: Subset<SceneItem>) {
      const scene = await this.super.duplicate(sceneId, changes);

      // duplicate flow nodes
      const flowNodeStore = useSceneFlowNodeStore();
      const flowNodes = flowNodeStore.byScene[sceneId] || {};
      for (const flowNode of Object.values(flowNodes)) {
        await flowNodeStore.duplicate(flowNode.id, { sceneId: scene.id });
      }

      // duplicate nodes
      const nodeStore = useSceneNodesStore();
      const sceneNodes = nodeStore.byScene[sceneId] || [];
      const rootNodes = sceneNodes.filter(n => n.parentSceneNodeUid === null);
      for (const rootNode of rootNodes) {
        await nodeStore.duplicate(rootNode.id, { sceneId: scene.id });
      }

      return scene;
    }
  },
  events: {
    async canBeDeleted(scene, store) {
      const scenes = store.orderedItems;
      if (scenes.length <= 1) {
        showMessage("Can't delete the remaining scene.");
        return false;
      }
      return true;
    },
    async afterDelete(scene) {
      const sceneNodeStore = useSceneNodesStore();
      // Delete all root nodes of the deleted scene (child nodes will be automatically deleted by cascade)
      const rootNodesToDelete = (sceneNodeStore.byScene[scene.id] || []).filter(node => node.parentSceneNodeUid === null);
      for (const node of rootNodesToDelete) {
        await sceneNodeStore.delete(node, { instant: true });
      }
    }
  }
});
